import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Lightbulb, Edit2, X, Save, Plus } from "lucide-react";
import type { CampaignReportResponse, InsightCallout } from "../types";
import { InsightsPanel } from "./InsightsPanel";
import { MarkdownEditor } from "./MarkdownEditor";
import { useAiInsights } from "../hooks/useAiInsights";
import { useReportCallouts } from "../hooks/useReportCallouts";

interface AiInsightsSectionProps {
  data: CampaignReportResponse;
  reportId?: string;
}

/**
 * Shared AI Insights + Callouts section used across campaign layouts.
 *
 * Handles:
 * - Dual-perspective insight generation (internal/external)
 * - Insight panel rendering + editing
 * - Key Callouts markdown editor with local persistence
 */
export const AiInsightsSection: React.FC<AiInsightsSectionProps> = ({
  data,
  reportId,
}) => {
  const {
    appendCallout,
    beginAdding,
    beginEditing,
    callouts,
    cancelEdit,
    deleteCallouts,
    isEditingCallouts,
    saveCallouts,
    setCallouts,
    showCallouts,
  } = useReportCallouts(reportId);
  const {
    dismissInsights,
    externalInsights,
    generateInsights,
    insightProgress,
    insightsError,
    insightsMetadata,
    internalInsights,
    isGeneratingInsights,
    markInsightAdded,
    perspectiveLoading,
    setExternalInsights,
    setInternalInsights,
  } = useAiInsights(data);

  // Add insight to Key Callouts
  const handleAddToCallouts = (
    insight: InsightCallout,
    perspective: "internal" | "external",
  ) => {
    const calloutText = `• **${insight.title}**: ${insight.description}`;
    appendCallout(calloutText);
    markInsightAdded(insight, perspective);
  };

  return (
    <>
      {(internalInsights.length > 0 ||
        externalInsights.length > 0 ||
        isGeneratingInsights) && (
        <div className="mb-8" data-exclude-from-export>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              AI-Powered Insights
            </h2>
            <button
              onClick={dismissInsights}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Dismiss
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <InsightsPanel
              insights={internalInsights}
              onInsightsChange={setInternalInsights}
              onAddToCallouts={(insight) =>
                handleAddToCallouts(insight, "internal")
              }
              isLoading={perspectiveLoading.internal}
              statusMessage={insightProgress.internal}
              generationTime={insightsMetadata.internalGenerationTime}
              modelUsed={insightsMetadata.internalModel}
              perspective="internal"
              title="Internal Perspective"
              description="Optimization opportunities and actionable improvements"
            />

            <InsightsPanel
              insights={externalInsights}
              onInsightsChange={setExternalInsights}
              onAddToCallouts={(insight) =>
                handleAddToCallouts(insight, "external")
              }
              isLoading={perspectiveLoading.external}
              statusMessage={insightProgress.external}
              generationTime={insightsMetadata.externalGenerationTime}
              modelUsed={insightsMetadata.externalModel}
              perspective="external"
              title="External Perspective"
              description="Client-facing wins and positive highlights"
            />
          </div>

          {insightsError && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-400">
              {insightsError}
            </p>
          )}
        </div>
      )}

      {internalInsights.length === 0 &&
        externalInsights.length === 0 &&
        !isGeneratingInsights && (
          <div className="mb-8" data-exclude-from-export>
            <button
              onClick={generateInsights}
              className="inline-flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
            >
              <Sparkles className="w-5 h-5" />
              Generate AI Insights
            </button>
            {insightsError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {insightsError}
              </p>
            )}
          </div>
        )}

      {(showCallouts || isEditingCallouts) && (
        <div
          className="mb-8 bg-blue-50 dark:bg-[#5B5353]/30 rounded-lg border-2 border-blue-200 dark:border-[#5B5353] p-6"
          data-export-include
        >
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-semibold text-blue-900 dark:text-[#FFF8F9] flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Key Callouts
            </h2>
            {!isEditingCallouts && (
              <div className="flex gap-2" data-exclude-from-export>
                <button
                  onClick={beginEditing}
                  className="p-2 text-blue-600 hover:text-blue-700 dark:text-[#FFF1F3] dark:hover:text-[#FFF8F9] hover:bg-blue-100 dark:hover:bg-[#5B5353] rounded-lg transition-colors"
                  title="Edit callouts"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={deleteCallouts}
                  className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-[#5B5353] rounded-lg transition-colors"
                  title="Delete callouts"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {isEditingCallouts ? (
            <div>
              <MarkdownEditor
                value={callouts}
                onChange={setCallouts}
                placeholder={`Add key insights, next steps, or important callouts here...\n\n• Use bullet points for better readability\n• **Bold** important metrics or trends\n• Include action items or recommendations\n\nTry markdown formatting: **bold**, _italic_, # heading, [links](url)`}
                className="mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={saveCallouts}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : callouts.trim() ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none text-blue-900 dark:text-[#FFF8F9]
                         prose-headings:text-blue-900 dark:prose-headings:text-[#FFF8F9]
                         prose-p:text-blue-800 dark:prose-p:text-[#FFF1F3]
                         prose-strong:text-blue-900 dark:prose-strong:text-[#FFF8F9]
                         prose-em:text-blue-800 dark:prose-em:text-[#FFF1F3]
                         prose-ul:text-blue-800 dark:prose-ul:text-[#FFF1F3]
                         prose-li:text-blue-800 dark:prose-li:text-[#FFF1F3]
                         [&_ul>li]:mb-3 [&_ul>li:last-child]:mb-0
                         prose-code:text-blue-700 dark:prose-code:text-[#B5A9AA]
                         prose-code:bg-blue-100 dark:prose-code:bg-[#5B5353]/30
                         prose-a:text-blue-600 dark:prose-a:text-[#FFF1F3]"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {callouts}
              </ReactMarkdown>
            </div>
          ) : null}
        </div>
      )}

      {!showCallouts && !isEditingCallouts && reportId && (
        <div className="mb-8" data-exclude-from-export>
          <button
            onClick={beginAdding}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Key Callouts
          </button>
        </div>
      )}
    </>
  );
};
